import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import type {
  InsurerOrganizationUnit,
  InsurerOrganizationUnitDiscount,
} from 'BrokerTsLib/src/dtos'
import type { InsuranceCategoryDiscount } from 'BrokerTsLib/src/dtos/OrganizationUnit/Discounts/InsuranceCategoryDiscount'
import { getProductFullName } from 'BrokerTsLib/src/enums'
import { useUpdateDiscount } from 'BrokerTsLib/src/hooks/useOrganizationUnit'
import { FormProvider, useForm } from 'BrokerTsLib/src/rhf'
import { useRef } from 'react'

import { DiscountEditForm } from './DiscountEditForm'

export const DiscountEditModal = ({
  discount,
  discounts,
  insurer,
  isOpen,
  onClose,
}: {
  discount: InsurerOrganizationUnitDiscount | InsuranceCategoryDiscount
  discounts: InsuranceCategoryDiscount[]
  insurer: InsurerOrganizationUnit
  isOpen: boolean
  onClose: () => void
}) => {

  const methods = useForm<InsurerOrganizationUnitDiscount>({
    defaultValues: {
      ...discount
    },
  })

  const { handleSubmit, reset, formState: { isDirty } } = methods
  const submitDialogLeastDestructiveRef = useRef<HTMLButtonElement>(null)
  const {
    isOpen: isSubmitDialogOpen,
    onClose: closeSubmitDialog,
    onOpen: openSubmitDialog,
  } = useDisclosure()
  const toast = useToast()
  const { mutateAsync: updateDiscount, isPending: isUpdating } =
    useUpdateDiscount()

  const handleSave = (value: InsurerOrganizationUnitDiscount) =>
    updateDiscount({
      organizationUnitId: insurer.organizationUnitId,
      insurerId: insurer.insurerId,
      insuranceCategoryType: insurer.insuranceCategoryType,
      ...value,
    })
      .then((r) => {
        toast({
          title: 'Sleva byla uložena',
          status: 'success',
        })

        return r
      })
      .catch((e) => {
        toast({
          title: 'Chyba při ukládání slevy',
          status: 'error',
        })

        return e
      })

  const handleClose = () => {
    if (isDirty) {
      openSubmitDialog()
    } else {
      onClose()
    }
  }

  const conflictDiscounts = discount.conflictDiscountIds
    .map((cd) => discounts.find((d) => d.insuranceCategoryDiscountId === cd))
    .filter((d) => !!d)

  const submitHandler = (data: InsurerOrganizationUnitDiscount) => handleSave(data)
    .then(() => reset(data))

  return (
    <>
      <Modal size="xl" isOpen={isOpen} onClose={handleClose} isCentered>
        <ModalOverlay />

        <ModalContent>
          <ModalHeader>
            <Text sx={{ my: 1 }} fontSize='sm' fontWeight='normal'>
              {getProductFullName(insurer.insuranceCategoryType)}
            </Text>
            <Text>{insurer.insurer}</Text>
            {discount.name && <Heading my={2}>{discount.name}</Heading>}

            <ModalCloseButton />
          </ModalHeader>

          <ModalBody sx={{ pt: 0 }}>
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(submitHandler)}>
                <DiscountEditForm
                  {...discount}
                  conflictDiscounts={conflictDiscounts}
                />
              </form>
            </FormProvider>
          </ModalBody>
        </ModalContent>
      </Modal>
      <AlertDialog
        isOpen={isSubmitDialogOpen}
        onClose={closeSubmitDialog}
        leastDestructiveRef={submitDialogLeastDestructiveRef}
      >
        <AlertDialogOverlay />

        <AlertDialogContent alignSelf="center">
          <AlertDialogHeader>Neuložené změny</AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            Přejete si uložit změny nebo je zahodit?
          </AlertDialogBody>
          <AlertDialogFooter gap={2}>
            <Button
              colorScheme="red"
              mr={'auto'}
              onClick={() => {
                reset(discount)
                closeSubmitDialog()
                onClose()
              }}
              isDisabled={isUpdating}
            >
              Zahodit změny
            </Button>
            <Button
              ref={submitDialogLeastDestructiveRef}
              onClick={closeSubmitDialog}
              isDisabled={isUpdating}
              colorScheme='gray'
            >
              Zpět
            </Button>
            <Button
              colorScheme="primary"
              onClick={() =>
                handleSubmit((data) => submitHandler(data).then(onClose))()
                  .finally(() => closeSubmitDialog())
              }
              isLoading={isUpdating}
            >
              Uložit změny
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog >
    </>
  )
}
